import * as React from "react";
import { RouteComponentProps } from "react-router";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "src/domain/model/app_notification_message";
import { FrontDate } from "src/domain/model/frontDate";
import { PartnerStoreEntryAPI } from "src/infra/api/service/partner_store_entry_api";
import { createUserAPI } from "src/infra/api/service/user_api";
import BtnCorporateLarge from "src/presentation/common/atom/btn/btn_corporate_large";
import FormFieldWrapper from "src/presentation/common/atom/wrapper/form_field_wrapper";
import SubmitButtonWrapper from "src/presentation/common/atom/wrapper/submit_btn_wrapper/submit_btn_wrapper";
import AppNotificationWithTexts from "src/presentation/common/organism/app_notification_with_texts";
import styled from "styled-components";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { api, entity } from "../../infra/api/rpc/api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import { TBreadcrumbItem } from "../type/common";

const apiClient: IApiClient = createApiClient();
const partnerStoreEntryAPI = new PartnerStoreEntryAPI(apiClient);
const userAPI = createUserAPI(apiClient);

interface IProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const CustomerPartnerStoreForm: React.FC<IProps> = (props) => {
  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.customerRoot,
    { text: `パートナーストア` },
    { text: `申し込みフォーム` },
  ];
  const [params, setParams] =
    React.useState<api.ICreatePartnerStoreEntryRequest>({
      partnerID: props.match.params.id,
    });
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      partnerStoreEntryAPI.create(params).then(() => {
        setParams({
          partnerID: props.match.params.id,
        });

        setTimeout(() => {
          props.history.push("/customer/partner-store-list?success=true");
        }, 500);
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  };

  React.useEffect(() => {
    try {
      userAPI.getMe().then((user) => {
        setParams((prevParams) => ({
          ...prevParams,
          customerName: user.customer!!.name,
          customerEmail: user.email,
          customerPhoneNumber: user.customer!!.phoneNumber,
        }));
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <form
          name="CustomerPartnerStoreForm"
          onSubmit={handleSubmit}
          style={{ maxWidth: 600, margin: "0 auto" }}
        >
          <Header>紹介者の情報</Header>
          <FormFieldWrapper>
            <Label>エキスパート名</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.customerName!!}
                onChange={(e) => {
                  setParams({ ...params, customerName: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>メールアドレス</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.customerEmail!!}
                onChange={(e) => {
                  setParams({ ...params, customerEmail: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>電話番号</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.customerPhoneNumber!!}
                onChange={(e) => {
                  setParams({ ...params, customerPhoneNumber: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>

          <Header>紹介先企業の情報</Header>
          <FormFieldWrapper>
            <Label>紹介先企業名</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.referralCompanyName!!}
                placeholder="紹介株式会社"
                onChange={(e) => {
                  setParams({ ...params, referralCompanyName: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>担当者名</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.inChargeUserName!!}
                placeholder="山田 太郎"
                onChange={(e) => {
                  setParams({ ...params, inChargeUserName: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>部署</Label>
            <div>
              <Input
                type="text"
                value={params.inChargeUserDepartment!!}
                placeholder="ビジネス統括部"
                onChange={(e) => {
                  setParams({
                    ...params,
                    inChargeUserDepartment: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>役職</Label>
            <div>
              <Input
                type="text"
                value={params.inChargeUserPosition!!}
                placeholder="部長"
                onChange={(e) => {
                  setParams({
                    ...params,
                    inChargeUserPosition: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>企業ホームページ</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.referralCompanyURL!!}
                placeholder=""
                onChange={(e) => {
                  setParams({
                    ...params,
                    referralCompanyURL: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>会社住所</Label>
            <div>
              <Input
                type="text"
                value={params.referralCompanyAddress!!}
                placeholder=""
                onChange={(e) => {
                  setParams({
                    ...params,
                    referralCompanyAddress: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>担当者メールアドレス</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.inChargeUserEmail!!}
                placeholder="yamada.taro@libera-inc.com"
                onChange={(e) => {
                  setParams({ ...params, inChargeUserEmail: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>担当者電話番号</Label>
            <div>
              <Input
                type="text"
                value={params.inChargeUserPhoneNumber!!}
                placeholder="0801234567"
                onChange={(e) => {
                  setParams({
                    ...params,
                    inChargeUserPhoneNumber: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>導入希望時期</Label>
            <Required />
            <div>
              <Select
                value={
                  params.desiredIntroductionPeriod
                    ? params.desiredIntroductionPeriod.toString()
                    : ""
                }
                onChange={(e) => {
                  let v =
                    entity.PartnerStoreEntry.DesiredIntroductionPeriod
                      .DesiredIntroductionPeriod_1_3_Month;
                  if (e.target.value === "1") {
                    v =
                      entity.PartnerStoreEntry.DesiredIntroductionPeriod
                        .DesiredIntroductionPeriod_1_3_Month;
                  } else if (e.target.value === "2") {
                    v =
                      entity.PartnerStoreEntry.DesiredIntroductionPeriod
                        .DesiredIntroductionPeriod_3_6_Month;
                  } else if (e.target.value === "3") {
                    v =
                      entity.PartnerStoreEntry.DesiredIntroductionPeriod
                        .DesiredIntroductionPeriod_Over_6_Month;
                  } else if (e.target.value === "4") {
                    v =
                      entity.PartnerStoreEntry.DesiredIntroductionPeriod
                        .DesiredIntroductionPeriod_Undecided;
                  } else {
                    return;
                  }

                  setParams({
                    ...params,
                    desiredIntroductionPeriod: v,
                  });
                }}
              >
                <option>---</option>
                <option value="1">1~3ヶ月</option>
                <option value="2">3~6ヶ月</option>
                <option value="3">6ヶ月以上</option>
                <option value="4">未定</option>
              </Select>
            </div>
          </FormFieldWrapper>

          <SubmitButtonWrapper>
            <BtnCorporateLarge
              name="CustomerPartnerStoreForm-submit"
              type="submit"
              widthSize="200px"
              disabled={
                !params.customerName ||
                !params.customerEmail ||
                !params.customerPhoneNumber ||
                !params.referralCompanyName ||
                !params.referralCompanyURL ||
                !params.inChargeUserName ||
                !params.inChargeUserEmail ||
                !params.desiredIntroductionPeriod
              }
            >
              送信
            </BtnCorporateLarge>
          </SubmitButtonWrapper>
        </form>
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerPartnerStoreForm;

const Header = styled.h2`
  margin-bottom: 20px;
  margin-top: 40px;
`;

const Label = styled.label`
  display: inline-block;
  margin-bottom: 10px;
`;

const Input = styled.input`
  width: 100%;
  height: 50px;
  padding: 0 10px;
`;

const Required = styled.span`
  margin-left: 5px;
  background-color: #ce4141;
  width: 5px;
  height: 5px;
  display: inline-block;
  border-radius: 50%;
  vertical-align: middle;
`;

const Select = styled.select`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  padding: 8px 20px;
  border: 1px solid #a4a4a4;
  box-sizing: border-box;
  border-radius: 3px;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 23px;
  background-color: #ffffff;
  color: #333333;
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAGCAYAAAD37n+BAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAACNSURBVHgBfY8xDsIwDEXr9AKsuQVVlJ3eAEZuxm1YYYjiIzAlsNEDJKl/VVVR1dZLHP/3v2xqpIwxT3kuzXGxc65T6FJKt1LKZ4+EJkyPfjIw8z/n3G+ZMIMGBn+qRWvtWUSsd5phBHUCL0FtbQghfLXWPyK6TmlEd+/9q2ba9QoxRhbToJR6y5GPtT4CsDRIeMuP1hEAAAAASUVORK5CYII=);
  background-repeat: no-repeat;
  background-size: 10px 6px;
  background-position: right 10px center;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_partner_store_form.tsx